import { useState } from 'react';
import { ChartBarIcon, ChevronRightIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { WithTooltip } from '../../../components/WithTooltip';
import { Mixpanel } from '../../../services/Mixpanel';
import { useDropdownPosition } from '../hooks/useDropdownPosition';
import { useUnlimitedContext } from '../contexts/UnlimitedContext';
import readingLevels from '../../../../public/json/reading-levels.json';
import languages from '../../../../public/json/languages.json';
import PremiumIconCrown from '../../../components/PremiumIconCrown';
import ExportUpsellCard from '../../../components/ExportUpsellCard';
import { useExportGate, useTrialBannerViewed } from '../../../utils/exportTrialState';
import { isHoveringPointer } from '../utils/isHoveringPointer';

/**
 * Dropdown menu for selecting reading levels and languages to adapt generated content.
 * Supports multi-select with submenus. Reads all state from UnlimitedContext.
 */
// Display labels for the MTSS tiers — kept here (not in a constants file) because they
// only appear in this menu. Backend whitelists 'tier_2' / 'tier_3' as overlay ids;
// 'tier_1' is a frontend-only explicit choice that the context collapses to null on the wire.
const MTSS_TIER_OPTIONS = [
  {
    id: 'tier_1',
    name: 'Tier 1 — Universal',
    description: 'No additional scaffolds.',
  },
  {
    id: 'tier_2',
    name: 'Tier 2 — Targeted',
    description: 'Add scaffolds: vocab pre-teach, sentence frames, guided practice',
  },
  {
    id: 'tier_3',
    name: 'Tier 3 — Intensive',
    description: 'Heavily scaffolded: chunked text, explicit modeling, repeated practice',
  },
];

// Source-text handling — same shape as MTSS_TIER_OPTIONS above, but includes the
// 'auto' default in the list (it renders gray with a "default" tag). The ids map to
// the keep_original_text payload field: 'on' → true, 'off' → false, 'auto' → omitted.
const ORIGINAL_TEXT_OPTIONS = [
  {
    id: 'auto',
    name: 'Auto',
    description: 'Include provided text based on your request',
  },
  {
    id: 'on',
    name: 'Include',
    description: 'Add provided text to the packet exactly as written',
  },
  {
    id: 'off',
    name: 'Exclude',
    description: "Don't include provided text in the packet",
  },
];

// tooltipPosition: 'top' under an input at the bottom of the screen (Courses).
export const AdaptMenu = ({ tooltipPosition = 'bottom' } = {}) => {
  const { toolbar, adapt, mtss, requireAuthForMenus, trackingSource } = useUnlimitedContext();
  // Another surface (Courses) tags its events; the create screen's stay exactly as they were.
  const track = (name, props) =>
    Mixpanel.track(name, trackingSource ? { ...props, source: trackingSource } : props);
  const { showAdaptMenu, setShowAdaptMenu, adaptSubmenuMobileStyle, setAdaptSubmenuMobileStyle } =
    toolbar;
  const {
    selectedReadingLevels,
    setSelectedReadingLevels,
    selectedLanguages,
    setSelectedLanguages,
    totalAdaptSelections,
    adaptAtCapacity,
    originalTextMode,
    setOriginalTextMode,
    originalTextSourceAttached,
  } = adapt;
  const { selectedMtssTier, setSelectedMtssTier } = mtss;
  const { tier, gateActive, showPremiumCrowns, showTrialCountdown } = useExportGate();
  const { menuRef, dropUp, leftOffset, maxHeight, recalculate } = useDropdownPosition({
    dropdownWidth: 256,
    minSpaceBelow: 320,
  });
  // Flyouts open from the panel's top edge, or from its bottom edge when the menu opens
  // upward (an input at the bottom of the screen), so they stay on screen.
  const flyoutEdge = dropUp ? 'bottom-0' : 'md:top-0';
  const [showReadingLevelSubmenu, setShowReadingLevelSubmenu] = useState(false);
  const [showLanguageSubmenu, setShowLanguageSubmenu] = useState(false);
  const [showMtssSubmenu, setShowMtssSubmenu] = useState(false);
  const [showOriginalTextSubmenu, setShowOriginalTextSubmenu] = useState(false);
  const [languageSearch, setLanguageSearch] = useState('');
  const [limitTooltip, setLimitTooltip] = useState({ x: 0, y: 0, visible: false });

  // bts-2026-freemium premium treatment for the MTSS selector (FE-only, additive).
  // Crowns show for free + trial; free additionally locks every row (look, can't pick);
  // trial keeps them selectable. Paid / flag-off ⇒ all false ⇒ unchanged. gateActive
  // (flag on + LD identified) is required — `tier` alone is not flag-gated.
  const mtssLocked = gateActive && tier === 'free';
  const trackEvent = (name, props) => track(name, props);
  useTrialBannerViewed(trackEvent, showTrialCountdown && showMtssSubmenu, 'mtss_menu');

  // Helper used by the close-overlay and clicks outside any row — collapses every submenu.
  const closeAllSubmenus = () => {
    setShowReadingLevelSubmenu(false);
    setShowLanguageSubmenu(false);
    setShowMtssSubmenu(false);
    setShowOriginalTextSubmenu(false);
    setLanguageSearch('');
  };

  const mtssSelectedLabel = (() => {
    if (!selectedMtssTier) return 'Auto';
    const opt = MTSS_TIER_OPTIONS.find((o) => o.id === selectedMtssTier);
    return opt ? opt.name.split(' — ')[0] : 'Auto';
  })();

  const hasMtssSelection = !!selectedMtssTier;
  // The mode only works with an attached source — the row is always visible (flag
  // permitting) but disabled until one is attached. Removing the source clears the
  // mode (the context owns that), so a disabled row always reads Auto.
  const originalTextDisabled = !originalTextSourceAttached;
  const isOriginalTextActive = originalTextMode === 'on' || originalTextMode === 'off';
  const originalTextPillLabel =
    ORIGINAL_TEXT_OPTIONS.find((o) => o.id === originalTextMode)?.name || 'Auto';
  const hasAnyAdaptCustomization =
    totalAdaptSelections > 0 || hasMtssSelection || isOriginalTextActive;

  return (
    <div className='relative' ref={menuRef}>
      <WithTooltip
        text={showAdaptMenu ? '' : 'Set reading level and language'}
        position={tooltipPosition}
        anchor='center'
        size='xs'
        showArrow>
        <button
          onClick={() => {
            requireAuthForMenus(() => {
              if (!showAdaptMenu) {
                track('Unlimited Set Reading Level Language Clicked');
                recalculate();
                // On mobile, extend submenus to viewport right edge
                const rect = menuRef.current?.getBoundingClientRect();
                if (rect && window.innerWidth < 848) {
                  const overflow = rect.left + 256 - window.innerWidth + 16;
                  const freshLeftOffset = overflow > 0 ? -overflow : 0;
                  const parentRight = rect.left + freshLeftOffset + 256;
                  const spaceRight = Math.max(0, window.innerWidth - 16 - parentRight);
                  setAdaptSubmenuMobileStyle({
                    right: -spaceRight,
                    width: 224 + spaceRight,
                  });
                } else {
                  setAdaptSubmenuMobileStyle(null);
                }
              } else {
                closeAllSubmenus();
              }
              setShowAdaptMenu((prev) => !prev);
            });
          }}
          className={`group flex items-center gap-1.5 py-2 px-3 rounded-xl transition-colors md:min-w-[5rem] max-w-[10rem] md:max-w-[14rem] ${
            hasAnyAdaptCustomization
              ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-100'
              : 'text-gray-600 hover:bg-gray-100 border border-transparent'
          }`}
          aria-label='Set reading level and language'>
          <ChartBarIcon className='w-5 h-5 flex-shrink-0' />
          <span className='text-sm flex items-baseline min-w-0 overflow-hidden'>
            <span className='shrink-0 hidden md:inline'>Adapt</span>
            {hasAnyAdaptCustomization && (
              <span className='hidden md:contents'>
                {(() => {
                  const numLevels = selectedReadingLevels.length;
                  const numLangs = selectedLanguages.length;
                  const mtssTag = hasMtssSelection ? mtssSelectedLabel : null;

                  const renderWithMtss = (levelLangText) => {
                    if (!mtssTag) return levelLangText;
                    if (!levelLangText) {
                      return <span className='shrink-0'>{`: ${mtssTag}`}</span>;
                    }
                    return (
                      <>
                        {levelLangText}
                        <span className='shrink-0'>{`, ${mtssTag}`}</span>
                      </>
                    );
                  };

                  if (numLevels === 0 && numLangs === 0) {
                    return renderWithMtss(null);
                  }

                  // Only grades
                  if (numLevels > 0 && numLangs === 0) {
                    const names =
                      numLevels <= 3
                        ? selectedReadingLevels
                            .map((l) => (numLevels > 1 ? l.name.replace(' Grade', '') : l.name))
                            .join(', ')
                        : selectedReadingLevels[0].name.replace(' Grade', '');
                    const remaining = numLevels > 3 ? numLevels - 1 : 0;
                    return renderWithMtss(
                      <span className='shrink-0'>
                        {`: ${names}${remaining > 0 ? ` & ${remaining} more` : ''}`}
                      </span>
                    );
                  }

                  // Only languages (1)
                  if (numLevels === 0 && numLangs === 1) {
                    return renderWithMtss(
                      <span className='truncate'>{`: ${selectedLanguages[0].name}`}</span>
                    );
                  }

                  // Only languages (2+)
                  if (numLevels === 0) {
                    return renderWithMtss(
                      <>
                        <span className='truncate'>{`: ${selectedLanguages[0].name}`}</span>
                        <span className='shrink-0 ml-1'>{`& ${numLangs - 1} more`}</span>
                      </>
                    );
                  }

                  // 1 grade + 1 language
                  if (numLevels === 1 && numLangs === 1) {
                    return renderWithMtss(
                      <>
                        <span className='shrink-0'>
                          {`: ${selectedReadingLevels[0].name.replace(' Grade', '')},`}
                        </span>
                        <span className='truncate ml-1'>{selectedLanguages[0].name}</span>
                      </>
                    );
                  }

                  // 1 grade + multiple languages
                  if (numLevels === 1) {
                    return renderWithMtss(
                      <span className='shrink-0'>
                        {`: ${selectedReadingLevels[0].name.replace(' Grade', '')} & ${numLangs} more`}
                      </span>
                    );
                  }

                  // Multiple grades + any languages
                  const shown = selectedReadingLevels.slice(0, 3);
                  const remaining = numLevels - shown.length + numLangs;
                  const names = shown.map((l) => l.name.replace(' Grade', '')).join(', ');
                  return renderWithMtss(
                    <span className='shrink-0'>
                      {`: ${names}${remaining > 0 ? ` & ${remaining} more` : ''}`}
                    </span>
                  );
                })()}
              </span>
            )}
          </span>
        </button>
      </WithTooltip>
      {showAdaptMenu && (
        <>
          <div
            className='fixed inset-0 z-40'
            onClick={() => {
              setShowAdaptMenu(false);
              closeAllSubmenus();
            }}
          />
          <div
            className={`absolute flex flex-col w-64 bg-white rounded-xl shadow-lg border border-gray-200 p-1.5 z-50 ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
            style={{ left: leftOffset, maxHeight }}
            onClick={closeAllSubmenus}>
            {/* Rows + tip scroll here; the side submenus below are NOT inside this wrapper so the
                scroll container can't clip them (overflow-y-auto would force overflow-x to clip a
                left-full submenu). maxHeight lives on the outer box so its padding is counted. */}
            <div className='min-h-0 overflow-y-auto overscroll-contain scrollbar-dropdown'>
              {/* Reading Level row */}
              <div
                onPointerEnter={(e) => {
                  if (isHoveringPointer(e)) {
                    setShowReadingLevelSubmenu(true);
                    setShowLanguageSubmenu(false);
                    setShowMtssSubmenu(false);
                    setShowOriginalTextSubmenu(false);
                    setLanguageSearch('');
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowReadingLevelSubmenu((prev) => !prev);
                  setShowLanguageSubmenu(false);
                  setShowMtssSubmenu(false);
                  setShowOriginalTextSubmenu(false);
                  setLanguageSearch('');
                }}
                className='w-full flex items-center justify-between px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm cursor-default'>
                <span>Reading Level</span>
                <span className='flex items-center gap-1.5 min-w-0'>
                  <span
                    className={`${selectedReadingLevels.length > 0 ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-100 text-gray-600'} inline-flex items-center min-w-0 px-2 py-0.5 rounded-full text-xs max-w-[7rem]`}>
                    <span className='truncate'>
                      {selectedReadingLevels.length > 0
                        ? selectedReadingLevels.length <= 3
                          ? selectedReadingLevels
                              .map((l) =>
                                selectedReadingLevels.length > 1
                                  ? l.name.replace(' Grade', '')
                                  : l.name
                              )
                              .join(', ')
                          : `${selectedReadingLevels[0].name.replace(' Grade', '')} & ${selectedReadingLevels.length - 1} more`
                        : 'Auto'}
                    </span>
                    {selectedReadingLevels.length > 0 && (
                      <button
                        type='button'
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedReadingLevels([]);
                          track('Unlimited Reading Level Language Set', {
                            reading_levels: [],
                            languages: selectedLanguages.map((l) => l.name),
                          });
                        }}
                        className='-my-0.5 -mr-2 ml-0.5 flex items-center justify-center self-stretch w-5 shrink-0 rounded-full hover:bg-emerald-200 transition-colors'
                        aria-label='Clear reading levels'>
                        <XMarkIcon className='w-2.5 h-2.5' />
                      </button>
                    )}
                  </span>
                  <ChevronRightIcon className='w-3.5 h-3.5 text-gray-400 shrink-0' />
                </span>
              </div>
              {/* Language row */}
              <div
                onPointerEnter={(e) => {
                  if (isHoveringPointer(e)) {
                    setShowLanguageSubmenu(true);
                    setShowReadingLevelSubmenu(false);
                    setShowMtssSubmenu(false);
                    setShowOriginalTextSubmenu(false);
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLanguageSubmenu((prev) => !prev);
                  setShowReadingLevelSubmenu(false);
                  setShowMtssSubmenu(false);
                  setShowOriginalTextSubmenu(false);
                }}
                className='w-full flex items-center justify-between px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm cursor-default'>
                <span>Language</span>
                <span className='flex items-center gap-1.5 min-w-0'>
                  <span
                    className={`${selectedLanguages.length > 0 ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-100 text-gray-600'} inline-flex items-center min-w-0 px-2 py-0.5 rounded-full text-xs ${selectedLanguages.length > 1 ? 'max-w-[10rem]' : 'max-w-[7rem]'}`}>
                    {selectedLanguages.length > 1 ? (
                      <>
                        <span className='truncate'>{selectedLanguages[0].name}</span>
                        <span className='shrink-0 whitespace-nowrap ml-1'>
                          {`& ${selectedLanguages.length - 1} more`}
                        </span>
                      </>
                    ) : (
                      <span className='truncate'>
                        {selectedLanguages.length === 1 ? selectedLanguages[0].name : 'Auto'}
                      </span>
                    )}
                    {selectedLanguages.length > 0 && (
                      <button
                        type='button'
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLanguages([]);
                          setLanguageSearch('');
                          track('Unlimited Reading Level Language Set', {
                            reading_levels: selectedReadingLevels.map((l) => l.name),
                            languages: [],
                          });
                        }}
                        className='-my-0.5 -mr-2 ml-0.5 flex items-center justify-center self-stretch w-5 shrink-0 rounded-full hover:bg-emerald-200 transition-colors'
                        aria-label='Clear languages'>
                        <XMarkIcon className='w-2.5 h-2.5' />
                      </button>
                    )}
                  </span>
                  <ChevronRightIcon className='w-3.5 h-3.5 text-gray-400 shrink-0' />
                </span>
              </div>
              {/* MTSS Tier row */}
              <div
                onPointerEnter={(e) => {
                  if (isHoveringPointer(e)) {
                    setShowMtssSubmenu(true);
                    setShowReadingLevelSubmenu(false);
                    setShowLanguageSubmenu(false);
                    setShowOriginalTextSubmenu(false);
                    setLanguageSearch('');
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMtssSubmenu((prev) => !prev);
                  setShowReadingLevelSubmenu(false);
                  setShowLanguageSubmenu(false);
                  setShowOriginalTextSubmenu(false);
                  setLanguageSearch('');
                }}
                className='w-full flex items-center justify-between px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm cursor-default'>
                <span>MTSS Tier</span>
                <span className='flex items-center gap-1.5 min-w-0'>
                  {showPremiumCrowns && (
                    <PremiumIconCrown className='h-3.5 w-3.5' wrapperClassName='flex-shrink-0' />
                  )}
                  <span
                    className={`${selectedMtssTier ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-100 text-gray-600'} inline-flex items-center min-w-0 px-2 py-0.5 rounded-full text-xs max-w-[7rem]`}>
                    <span className='truncate'>{mtssSelectedLabel}</span>
                    {selectedMtssTier && (
                      <button
                        type='button'
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMtssTier(null);
                          track('Unlimited MTSS Tier Set', { mtss_tier: null });
                        }}
                        className='-my-0.5 -mr-2 ml-0.5 flex items-center justify-center self-stretch w-5 shrink-0 rounded-full hover:bg-emerald-200 transition-colors'
                        aria-label='Clear MTSS tier'>
                        <XMarkIcon className='w-2.5 h-2.5' />
                      </button>
                    )}
                  </span>
                  <ChevronRightIcon className='w-3.5 h-3.5 text-gray-400 shrink-0' />
                </span>
              </div>
              {/* Original Text row — always opens the submenu; with no source
                attached the row renders muted and the submenu's options are inert, with a
                tip explaining how to activate them. */}
              <div
                onPointerEnter={(e) => {
                  if (isHoveringPointer(e)) {
                    setShowOriginalTextSubmenu(true);
                    setShowReadingLevelSubmenu(false);
                    setShowLanguageSubmenu(false);
                    setShowMtssSubmenu(false);
                    setLanguageSearch('');
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOriginalTextSubmenu((prev) => !prev);
                  setShowReadingLevelSubmenu(false);
                  setShowLanguageSubmenu(false);
                  setShowMtssSubmenu(false);
                  setLanguageSearch('');
                }}
                className={`w-full flex items-center justify-between px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors text-sm cursor-default ${
                  originalTextDisabled ? 'text-gray-400' : 'text-gray-700'
                }`}>
                <span>Source Text</span>
                <span className='flex items-center gap-1.5 shrink-0'>
                  <span
                    className={`${
                      isOriginalTextActive
                        ? 'bg-emerald-50 text-emerald-800'
                        : originalTextDisabled
                          ? 'bg-gray-50 text-gray-400'
                          : 'bg-gray-100 text-gray-600'
                    } inline-flex items-center min-w-0 px-2 py-0.5 rounded-full text-xs max-w-[7rem]`}>
                    <span className='truncate'>{originalTextPillLabel}</span>
                    {isOriginalTextActive && (
                      <button
                        type='button'
                        onClick={(e) => {
                          e.stopPropagation();
                          setOriginalTextMode('auto');
                          track('Unlimited Original Text Mode Set', { mode: 'auto' });
                        }}
                        className='-my-0.5 -mr-2 ml-0.5 flex items-center justify-center self-stretch w-5 shrink-0 rounded-full hover:bg-emerald-200 transition-colors'
                        aria-label='Reset source text to auto'>
                        <XMarkIcon className='w-2.5 h-2.5' />
                      </button>
                    )}
                  </span>
                  <ChevronRightIcon
                    className={`w-3.5 h-3.5 shrink-0 ${originalTextDisabled ? 'text-gray-300' : 'text-gray-400'}`}
                  />
                </span>
              </div>
              {/* Info note */}
              <div
                onMouseEnter={() => {
                  setShowReadingLevelSubmenu(false);
                  setShowLanguageSubmenu(false);
                  setShowMtssSubmenu(false);
                  setShowOriginalTextSubmenu(false);
                }}
                className='text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 mt-1'>
                {totalAdaptSelections === 0
                  ? "We'll automatically level and scaffold based on your profile and prompt"
                  : adaptAtCapacity
                    ? `${totalAdaptSelections} of 5 levels and languages selected`
                    : 'Select up to 5 levels and languages'}
              </div>
            </div>
            {/* Reading Level submenu */}
            {showReadingLevelSubmenu && (
              <div
                className={`absolute top-auto ${adaptSubmenuMobileStyle ? '' : 'right-0 w-56'} md:right-auto md:left-full ${flyoutEdge} md:-ml-2 md:w-48 max-h-64 overflow-y-auto overscroll-contain scrollbar-dropdown bg-white rounded-xl shadow-lg border border-gray-200 p-1.5 z-50`}
                style={adaptSubmenuMobileStyle || undefined}
                onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => {
                    setSelectedReadingLevels([]);
                    track('Unlimited Reading Level Language Set', {
                      reading_levels: [],
                      languages: selectedLanguages.map((l) => l.name),
                    });
                  }}
                  className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg transition-colors text-sm ${
                    selectedReadingLevels.length === 0
                      ? 'bg-gray-50 text-gray-900'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}>
                  <span>Automatic</span>
                  {selectedReadingLevels.length === 0 ? (
                    <CheckIcon className='w-4 h-4 text-gray-900' />
                  ) : (
                    <span className='text-gray-400 text-xs'>default</span>
                  )}
                </button>
                {readingLevels.map((level) => {
                  const isSelected = selectedReadingLevels.some((l) => l.id === level.id);
                  const isDisabled = !isSelected && adaptAtCapacity;
                  return (
                    <button
                      key={level.id}
                      aria-disabled={isDisabled}
                      onMouseMove={(e) => {
                        if (isDisabled) {
                          setLimitTooltip({
                            x: e.clientX,
                            y: e.clientY,
                            visible: true,
                          });
                        }
                      }}
                      onMouseLeave={() => setLimitTooltip((prev) => ({ ...prev, visible: false }))}
                      onClick={() => {
                        if (isDisabled) return;
                        let updated;
                        if (isSelected) {
                          updated = selectedReadingLevels.filter((l) => l.id !== level.id);
                        } else {
                          updated = [...selectedReadingLevels, level];
                        }
                        setSelectedReadingLevels(updated);
                        track('Unlimited Reading Level Language Set', {
                          reading_levels: updated.map((l) => l.name),
                          languages: selectedLanguages.map((l) => l.name),
                        });
                      }}
                      className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg transition-colors text-sm ${
                        isDisabled
                          ? 'text-gray-300 cursor-default'
                          : isSelected
                            ? 'bg-emerald-50 text-emerald-800'
                            : 'text-gray-700 hover:bg-gray-100'
                      }`}>
                      <span>{level.name}</span>
                      {isSelected && <CheckIcon className='w-4 h-4' />}
                    </button>
                  );
                })}
              </div>
            )}
            {/* Language submenu */}
            {showLanguageSubmenu && (
              <div
                className={`absolute top-auto ${adaptSubmenuMobileStyle ? '' : 'right-0 w-56'} md:right-auto md:left-full ${flyoutEdge} md:-ml-2 md:w-56 max-h-64 overflow-y-auto overscroll-contain scrollbar-dropdown bg-white rounded-xl shadow-lg border border-gray-200 pt-0 pb-1.5 px-1.5 z-50`}
                style={adaptSubmenuMobileStyle || undefined}
                onClick={(e) => e.stopPropagation()}>
                <div className='sticky top-0 bg-white pt-1.5 pb-1.5 px-0 z-10'>
                  <input
                    type='text'
                    value={languageSearch}
                    onChange={(e) => setLanguageSearch(e.target.value)}
                    placeholder='Search languages...'
                    className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-0 focus:border-gray-900'
                    autoFocus={window.innerWidth >= 848}
                  />
                </div>
                <button
                  onClick={() => {
                    setSelectedLanguages([]);
                    setLanguageSearch('');
                    track('Unlimited Reading Level Language Set', {
                      reading_levels: selectedReadingLevels.map((l) => l.name),
                      languages: [],
                    });
                  }}
                  className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg transition-colors text-sm ${
                    selectedLanguages.length === 0
                      ? 'bg-gray-50 text-gray-900'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}>
                  <span>Automatic</span>
                  {selectedLanguages.length === 0 ? (
                    <CheckIcon className='w-4 h-4 text-gray-900' />
                  ) : (
                    <span className='text-gray-400 text-xs'>default</span>
                  )}
                </button>
                {languages
                  .filter((lang) => lang.name.toLowerCase().includes(languageSearch.toLowerCase()))
                  .map((lang) => {
                    const isSelected = selectedLanguages.some((l) => l.id === lang.id);
                    const isDisabled = !isSelected && adaptAtCapacity;
                    return (
                      <button
                        key={lang.id}
                        aria-disabled={isDisabled}
                        onMouseMove={(e) => {
                          if (isDisabled) {
                            setLimitTooltip({
                              x: e.clientX,
                              y: e.clientY,
                              visible: true,
                            });
                          }
                        }}
                        onMouseLeave={() =>
                          setLimitTooltip((prev) => ({ ...prev, visible: false }))
                        }
                        onClick={() => {
                          if (isDisabled) return;
                          let updated;
                          if (isSelected) {
                            updated = selectedLanguages.filter((l) => l.id !== lang.id);
                          } else {
                            updated = [...selectedLanguages, lang];
                          }
                          setSelectedLanguages(updated);
                          track('Unlimited Reading Level Language Set', {
                            reading_levels: selectedReadingLevels.map((l) => l.name),
                            languages: updated.map((l) => l.name),
                          });
                        }}
                        className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg transition-colors text-sm ${
                          isDisabled
                            ? 'text-gray-300 cursor-default'
                            : isSelected
                              ? 'bg-emerald-50 text-emerald-800'
                              : 'text-gray-700 hover:bg-gray-100'
                        }`}>
                        <span>{lang.name}</span>
                        {isSelected && <CheckIcon className='w-4 h-4' />}
                      </button>
                    );
                  })}
              </div>
            )}
            {/* MTSS submenu — taller cap (max-h-80 vs the sibling submenus' -64) ONLY when the
                freemium upsell card is present, so the card isn't clipped below the scroll fold
                (rows + card ~300px, past max-h-64). Flag-off / paid keep max-h-64 so the menu is
                byte-identical to today. */}
            {showMtssSubmenu && (
              <div
                className={`absolute top-auto ${adaptSubmenuMobileStyle ? '' : 'right-0 w-64'} md:right-auto md:left-full ${flyoutEdge} md:-ml-2 md:w-64 ${showTrialCountdown || mtssLocked ? 'max-h-80' : 'max-h-64'} overflow-y-auto overscroll-contain scrollbar-dropdown bg-white rounded-xl shadow-lg border border-gray-200 p-1.5 z-50`}
                style={adaptSubmenuMobileStyle || undefined}
                onClick={(e) => e.stopPropagation()}>
                {/* Automatic is the free baseline — it always works, so it is never crowned
                    or locked (only the specific Tier 1/2/3 presets are the premium upgrade). */}
                <button
                  onClick={() => {
                    setSelectedMtssTier(null);
                    track('Unlimited MTSS Tier Set', { mtss_tier: null });
                  }}
                  className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg transition-colors text-sm ${
                    !selectedMtssTier
                      ? 'bg-gray-50 text-gray-900'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}>
                  <span>Automatic</span>
                  {!selectedMtssTier ? (
                    <CheckIcon className='w-4 h-4 text-gray-900' />
                  ) : (
                    <span className='text-gray-400 text-xs'>default</span>
                  )}
                </button>
                {MTSS_TIER_OPTIONS.map((tier) => {
                  const isSelected = selectedMtssTier === tier.id;
                  return (
                    <button
                      key={tier.id}
                      onClick={() => {
                        if (mtssLocked) return;
                        // Mutually exclusive — selecting a tier replaces any prior choice;
                        // clicking the active tier clears it back to Automatic.
                        const next = isSelected ? null : tier.id;
                        setSelectedMtssTier(next);
                        track('Unlimited MTSS Tier Set', { mtss_tier: next });
                      }}
                      className={`w-full flex items-start justify-between gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm text-left ${
                        isSelected
                          ? 'bg-emerald-50 text-emerald-800'
                          : 'text-gray-700 hover:bg-gray-100'
                      } ${mtssLocked ? 'opacity-50 pointer-events-none' : ''}`}>
                      <span className='flex flex-col'>
                        <span>{tier.name}</span>
                        <span className='text-xs text-gray-500'>{tier.description}</span>
                      </span>
                      <span className='flex items-center gap-1.5 mt-0.5 shrink-0'>
                        {isSelected && <CheckIcon className='w-4 h-4' />}
                        {showPremiumCrowns && (
                          <PremiumIconCrown
                            className='h-3.5 w-3.5'
                            wrapperClassName='flex-shrink-0'
                          />
                        )}
                      </span>
                    </button>
                  );
                })}
                {(showTrialCountdown || mtssLocked) && (
                  <div className='mt-1'>
                    <ExportUpsellCard
                      mode={showTrialCountdown ? 'trial' : 'free'}
                      location='mtss_menu'
                      trackEvent={trackEvent}
                      freeHeading='Need a specific MTSS tier?'
                    />
                  </div>
                )}
              </div>
            )}
            {/* Original Text submenu — opens even with no source attached; the options
                are then inert (so a mode can never be set without a source) and a tip
                explains how to activate them. */}
            {showOriginalTextSubmenu && (
              <div
                className={`absolute top-auto ${adaptSubmenuMobileStyle ? '' : 'right-0 w-64'} md:right-auto md:left-full ${flyoutEdge} md:-ml-2 md:w-64 max-h-64 overflow-y-auto overscroll-contain scrollbar-dropdown bg-white rounded-xl shadow-lg border border-gray-200 p-1.5 z-50`}
                style={adaptSubmenuMobileStyle || undefined}
                onClick={(e) => e.stopPropagation()}>
                {ORIGINAL_TEXT_OPTIONS.map((opt) => {
                  const isSelected = !originalTextDisabled && originalTextMode === opt.id;
                  const isAutoDefault = opt.id === 'auto';
                  return (
                    <button
                      key={opt.id}
                      aria-disabled={originalTextDisabled}
                      onClick={() => {
                        if (originalTextDisabled) return;
                        // Mutually exclusive — clicking the active non-default option
                        // resets back to 'auto' (matches MTSS Tier 2/3 behavior).
                        const next = !isAutoDefault && isSelected ? 'auto' : opt.id;
                        setOriginalTextMode(next);
                        track('Unlimited Original Text Mode Set', { mode: next });
                      }}
                      className={`w-full flex flex-col px-3 py-1.5 rounded-lg transition-colors text-sm text-left ${
                        originalTextDisabled
                          ? 'text-gray-300 cursor-default'
                          : isSelected
                            ? isAutoDefault
                              ? 'bg-gray-50 text-gray-900'
                              : 'bg-emerald-50 text-emerald-800'
                            : 'text-gray-700 hover:bg-gray-100'
                      }`}>
                      {/* Indicator shares a line with the name only — the description spans
                          the full width so its wrapping never changes with selection state. */}
                      <span className='w-full flex items-center justify-between gap-2'>
                        <span>{opt.name}</span>
                        {isSelected ? (
                          <CheckIcon
                            className={`w-4 h-4 shrink-0 ${isAutoDefault ? 'text-gray-900' : ''}`}
                          />
                        ) : isAutoDefault ? (
                          <span
                            className={`text-xs shrink-0 ${originalTextDisabled ? 'text-gray-300' : 'text-gray-400'}`}>
                            default
                          </span>
                        ) : null}
                      </span>
                      <span
                        className={`text-xs ${originalTextDisabled ? 'text-gray-300' : 'text-gray-500'}`}>
                        {opt.description}
                      </span>
                    </button>
                  );
                })}
                {originalTextDisabled && (
                  <div className='text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 mt-1'>
                    <span>Attach or paste a text to adjust source text settings</span>
                  </div>
                )}
              </div>
            )}
            {/* Cursor-following tooltip for disabled items */}
            {limitTooltip.visible && (
              <div
                className='fixed z-50 px-2 py-1 text-xs text-white bg-gray-800 rounded shadow-lg pointer-events-none'
                style={{ left: limitTooltip.x + 12, top: limitTooltip.y - 28 }}>
                {totalAdaptSelections} of 5 selected
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
